import { Alert, Box, Button, CloseButton, Text } from '@chakra-ui/react';
import parsePhoneNumber, { getCountryCallingCode } from 'libphonenumber-js/min';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatPhone } from '../../../../../../utils/intlTel';
import { useAppContext } from '../../../../context/appContext';
import { useProfileContext } from '../../context/profileContext';
import authenticationService from '../../../../../../service/authenticationService';
import { MOBILE_PHONE_STEP } from '../../../../constants';
import { FIELD } from '../../constants/field';
import { CONFIG, MESSAGE_TYPE } from '../../constants/message';
import FormField from '../FormField';
import FormIconHint from '../FormIconHint';
import PhoneField from '../PhoneField';
import MobileCheckProcess from './MobileCheckProcess';
import {
  getMobilePhoneDefaultCountry,
  getMobilePhoneCountriesFromUserSettings,
} from './utils';
import { getBlockedCountries } from '../../../../../../utils/intlTel';

const MobilePhoneField = () => {
  const { t } = useTranslation();
  const { userData, submitError } = useProfileContext();
  const { userSetting, requestCountry, merchantData } = useAppContext();
  const shouldMobilePhoneConfirmed = userSetting?.sms_verification?.status;
  const [step, setStep] = useState(MOBILE_PHONE_STEP.EMPTY);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messages, setMessages] = useState({ success: [], error: [] });
  const hasMessages = useMemo(
    () => Object.values(messages).some((items) => items.length > 0),
    [messages],
  );
  const allowedCountryCodes = getMobilePhoneCountriesFromUserSettings(
    userSetting,
    merchantData,
  );
  const mobilePhoneDefaultCountry = getMobilePhoneDefaultCountry(
    allowedCountryCodes,
    requestCountry,
  );
  const mobilePhoneDefaultCountryCallingCode = getCountryCallingCode(
    mobilePhoneDefaultCountry.toUpperCase(),
  );
  const { control, errors, clearErrors, register, setError, setValue, watch } =
    useFormContext();
  const countryController = useController({
    name: FIELD.COUNTRY_CALLING_CODE,
    control,
    defaultValue:
      userData[FIELD.COUNTRY_CALLING_CODE] ??
      mobilePhoneDefaultCountryCallingCode,
  });
  const mobilePhoneController = useController({
    name: FIELD.MOBILE_PHONE,
    control,
    defaultValue: userData[FIELD.MOBILE_PHONE] ?? '',
  });
  const {
    [FIELD.IS_MOBILE_PHONE_CONFIRMED]: isMobilePhoneConfirmedValue,
    [FIELD.PHONE]: phoneValue = '',
    [FIELD.PHONE_COUNTRY_CODE]: phoneCountryCodeValue = '',
  } = watch([
    FIELD.IS_MOBILE_PHONE_CONFIRMED,
    FIELD.PHONE,
    FIELD.PHONE_COUNTRY_CODE,
  ]);
  const parseMobileNumber = parsePhoneNumber(
    `+${countryController.field.value}${mobilePhoneController.field.value}`,
  );
  const parsedPhoneNumber = parsePhoneNumber(
    `+${phoneCountryCodeValue}${phoneValue}`,
  );
  const [countryCode, setCountryCode] = useState(
    parseMobileNumber?.country || mobilePhoneDefaultCountry,
  );
  const hasMobilePhone = Boolean(mobilePhoneController.field.value);

  useEffect(() => {
    register(FIELD.IS_MOBILE_PHONE_CONFIRMED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (submitError?.[FIELD.MOBILE_PHONE]) {
      setMessages((prev) => ({
        ...prev,
        error: [t(submitError[FIELD.MOBILE_PHONE])],
      }));
    }
  }, [submitError, t]);

  const handlePhoneNumberBlur = useCallback(
    (_, mobilePhone, { iso2 }) => {
      if (mobilePhone.length === 0 || errors[FIELD.MOBILE_PHONE]) return;
      setValue(FIELD.MOBILE_PHONE, formatPhone(mobilePhone, iso2), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [errors, setValue],
  );

  const handlePhoneNumberChange = useCallback(
    (_, mobilePhone) => {
      if (mobilePhoneController.field.value === mobilePhone) {
        return;
      }
      setValue(FIELD.MOBILE_PHONE, mobilePhone, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(FIELD.IS_MOBILE_PHONE_CONFIRMED, false);
    },
    [mobilePhoneController.field.value, setValue],
  );

  const handleCountryChange = useCallback(
    (currentNumber, { dialCode, iso2 }) => {
      const newCountry = iso2?.toLowerCase();
      if (newCountry !== countryCode.toLowerCase()) {
        setValue(FIELD.COUNTRY_CALLING_CODE, dialCode, { shouldDirty: true });
        setValue(FIELD.MOBILE_PHONE, formatPhone(currentNumber, newCountry), {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(FIELD.IS_MOBILE_PHONE_CONFIRMED, false);
        setCountryCode(newCountry);
      }
    },
    [countryCode, setValue],
  );

  const handleMessageDelete = useCallback((type, index) => {
    setMessages((prev) => ({
      ...prev,
      [type]: [...prev[type].slice(0, index), ...prev[type].slice(index + 1)],
    }));
  }, []);

  const shouldShowFillPhoneButton = useMemo(() => {
    const hasUserPhone = Boolean(
      parsedPhoneNumber?.formatNational() ?? phoneValue,
    );
    return !hasMobilePhone && hasUserPhone;
  }, [hasMobilePhone, parsedPhoneNumber, phoneValue]);

  const fillPhoneToMobilePhone = useCallback(() => {
    const { countryCallingCode, country } = parsedPhoneNumber || {};
    if (
      country &&
      Array.isArray(allowedCountryCodes) &&
      !allowedCountryCodes.includes(country.toLowerCase())
    ) {
      setError(FIELD.MOBILE_PHONE, {
        type: 'validate',
        message: 'session.forget_passowrd.check_phone.error',
      });
      return;
    }
    setValue(
      FIELD.COUNTRY_CALLING_CODE,
      countryCallingCode ?? mobilePhoneDefaultCountryCallingCode,
      { shouldDirty: true },
    );
    setValue(FIELD.MOBILE_PHONE, phoneValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(FIELD.IS_MOBILE_PHONE_CONFIRMED, false);
    setCountryCode(country ?? mobilePhoneDefaultCountry);
  }, [
    parsedPhoneNumber,
    allowedCountryCodes,
    setValue,
    mobilePhoneDefaultCountryCallingCode,
    phoneValue,
    mobilePhoneDefaultCountry,
    setError,
  ]);

  const hasConfirmedMobilePhone =
    userData?.[FIELD.MOBILE_PHONE] && isMobilePhoneConfirmedValue;

  const verifyPhoneSubmit = useCallback(async () => {
    if (countryController.meta.invalid || mobilePhoneController.meta.invalid) {
      return;
    }
    const mobilePhone = mobilePhoneController.field.value || '';
    if (
      userData?.[FIELD.MOBILE_PHONE] === mobilePhone &&
      userData.mobile_phone_confirmed_at
    ) {
      setMessages((prev) => ({
        ...prev,
        error: [t('user.sign_up.check_phone.error')],
      }));
      return;
    }
    setIsSubmitting(true);
    const params = {
      mobilePhone,
      countryCallingCode: countryController.field.value,
    };

    try {
      await authenticationService.checkUserExistenceByPhone(params);
      setIsSubmitting(false);
      setMessages((prev) => ({
        ...prev,
        error: [t('session.mobile_center.check_phone.error')],
      }));
    } catch (error) {
      if (error?.response?.status === 404) {
        setStep(MOBILE_PHONE_STEP.CHECK_MOBILE);
        clearErrors(FIELD.MOBILE_PHONE);
      } else {
        setMessages((prev) => ({
          ...prev,
          error: [String(error?.response?.data?.error)],
        }));
      }
      setIsSubmitting(false);
    }
  }, [
    clearErrors,
    countryController.field.value,
    countryController.meta.invalid,
    mobilePhoneController.field.value,
    mobilePhoneController.meta.invalid,
    userData,
    t,
  ]);

  const handleVerifiedSuccess = useCallback(() => {
    setValue(FIELD.IS_MOBILE_PHONE_CONFIRMED, true);
    setMessages((prev) => ({
      ...prev,
      success: [t('membership.profile.mobile.number.verified')],
    }));
  }, [setValue, t]);

  const handleCheckMobileErrorMessages = useCallback(
    (messages) =>
      setMessages((prev) => ({
        ...prev,
        error: messages,
      })),
    [],
  );

  return (
    <div>
      {hasMessages &&
        Object.values(MESSAGE_TYPE).map((type) =>
          messages[type].map((message, index) => (
            <Alert
              borderRadius="4px"
              key={message}
              status={type}
              bg={CONFIG[type].bg}
              alignItems="center"
              justifyContent="space-between"
              p="15px"
              mb="20px"
            >
              <Text
                variant={CONFIG[type].variant}
                textAlign="center"
                fontSize="14px"
                flexGrow={1}
              >
                {message}
              </Text>
              <CloseButton
                opacity={0.2}
                color="#000"
                onClick={() => handleMessageDelete(type, index)}
              />
            </Alert>
          )),
        )}

      {step === MOBILE_PHONE_STEP.EMPTY && (
        <FormField label={t('users.membership.member_info.mobile')}>
          <PhoneField
            onCountryChange={handleCountryChange}
            onPhoneNumberChange={handlePhoneNumberChange}
            onPhoneNumberBlur={handlePhoneNumberBlur}
            onlyCountries={allowedCountryCodes}
            excludeCountries={getBlockedCountries()}
            defaultCountry={countryCode}
            value={mobilePhoneController.field.value}
            e2eId="member-center-profile-mobile-phone_field"
          />
          {shouldMobilePhoneConfirmed && isMobilePhoneConfirmedValue && (
            <FormIconHint
              isVerified
              icon={<i className="fa fa-icon fa-check" aria-hidden="true"></i>}
            >
              {t('membership.profile.verified_success')}
            </FormIconHint>
          )}
          {shouldMobilePhoneConfirmed &&
            !isMobilePhoneConfirmedValue &&
            hasMobilePhone && (
              <FormIconHint
                icon={
                  <i
                    className="fa fa-icon fa-exclamation-triangle"
                    aria-hidden="true"
                  />
                }
              >
                {t('membership.profile.not_verified_yet')}
              </FormIconHint>
            )}
          {shouldShowFillPhoneButton && (
            <Box textAlign="left" marginTop="2px">
              <Button
                variant="textButton"
                className="member-center-profile__text-button"
                onClick={fillPhoneToMobilePhone}
              >
                {t('membership.edit.select_saved_mobile_number')}
              </Button>
            </Box>
          )}
        </FormField>
      )}
      {errors?.[FIELD.MOBILE_PHONE]?.message && (
        <FormIconHint>{t(errors[FIELD.MOBILE_PHONE].message)}</FormIconHint>
      )}
      {shouldMobilePhoneConfirmed &&
        userData &&
        !hasConfirmedMobilePhone &&
        step === MOBILE_PHONE_STEP.EMPTY && (
          <FormField>
            <Button
              data-e2e-id="member-center-profile-send-code_button"
              variant="fillSuccess"
              isLoading={isSubmitting}
              isDisabled={!hasMobilePhone || mobilePhoneController.meta.invalid}
              onClick={verifyPhoneSubmit}
              isFullWidth
              marginTop="20px"
              className="member-center-profile__primary-button"
            >
              {t('membership.edit.send_me_check_mode')}
            </Button>
          </FormField>
        )}
      {shouldMobilePhoneConfirmed && !hasConfirmedMobilePhone && (
        <FormField>
          <MobileCheckProcess
            step={step}
            setStep={setStep}
            onVerifiedSuccess={handleVerifiedSuccess}
            setErrorMessages={handleCheckMobileErrorMessages}
          />
        </FormField>
      )}
    </div>
  );
};

export default MobilePhoneField;
